import { DropdownMenu, Icon, IconName } from '@usealloy/component-library';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { UserContext } from '~/dashboard/context';
import { routes } from '~/helpers/routes';

import { Collapsible } from '../component/Collapsible';
import { Sidebar } from '../component/Sidebar';
import { isEntityEvaluationActive, isJourneyActive } from './routeMatchers';
import { CollapsibleTriggerIcon, useCollapsibleNavItem } from './useCollapsibleNavItem';

// ============================================================
//     INTERNAL COMPONENTS
// ============================================================
/**
 * The `Records` nav item — Entities and Evaluations.
 *
 * Renders a collapsible submenu when the sidebar is expanded, otherwise a
 * right-side dropdown (FE2.0 `RecordsNavItem`; its Alerts entry is omitted here
 * since Daffy's Alerts item already lives in the Review group).
 */
const RecordsNavItem = () => {
  const { pathname } = useLocation();

  const isEntitiesActive = isEntityEvaluationActive(routes.entities.list, pathname);
  const isEvaluationsActive = isEntityEvaluationActive(
    routes.v3.dashboard.evaluations.list,
    pathname,
  );
  const isSubRouteActive = isEntitiesActive || isEvaluationsActive;

  const {
    isCollapsibleOpen,
    isDropdownOpen,
    isExpanded,
    isParentActive,
    setIsDropdownOpen,
    toggleCollapsible,
  } = useCollapsibleNavItem('SideNav.Records.isOpen', isSubRouteActive);

  const entitiesHref = `${window.location.origin}${routes.entities.list}`;
  const evaluationsHref = `${window.location.origin}${routes.v3.dashboard.evaluations.list}`;

  // Collapsed rail: the item becomes a right-side dropdown.
  // (No tooltip on the trigger — the component-library Tooltip and DropdownMenu
  // trigger wrappers can't be nested on the same button.)
  if (!isExpanded) {
    return (
      <Sidebar.MenuItem>
        <DropdownMenu
          align="start"
          isOpen={isDropdownOpen}
          onOpenChange={setIsDropdownOpen}
          side="right"
          trigger={(
            <Sidebar.MenuButton
              data-dd-action-name="alloydd-sidebar-nav-records"
              isActive={isParentActive}
            >
              <Icon icon={IconName.BoxArchive} size="small" />
              <span className="fs-unmask">Records</span>
            </Sidebar.MenuButton>
          )}
        >
          <DropdownMenu.Item
            data-dd-action-name="alloydd-sidebar-nav-entities"
            onSelect={() => window.location.assign(entitiesHref)}
          >
            Entities
          </DropdownMenu.Item>
          <DropdownMenu.Item
            data-dd-action-name="alloydd-sidebar-nav-evaluations"
            onSelect={() => window.location.assign(evaluationsHref)}
          >
            Evaluations
          </DropdownMenu.Item>
        </DropdownMenu>
      </Sidebar.MenuItem>
    );
  }

  return (
    <Sidebar.MenuItem>
      <Sidebar.MenuButton
        data-dd-action-name="alloydd-sidebar-nav-records"
        isActive={isParentActive}
        onClick={toggleCollapsible}
      >
        <CollapsibleTriggerIcon isOpen={isCollapsibleOpen} />
        <span className="fs-unmask">Records</span>
      </Sidebar.MenuButton>
      <Collapsible open={isCollapsibleOpen}>
        <Sidebar.Menu isSubmenu>
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              as="a"
              data-dd-action-name="alloydd-sidebar-nav-entities"
              href={entitiesHref}
              isActive={isEntitiesActive}
            >
              <span className="fs-unmask">Entities</span>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              as="a"
              data-dd-action-name="alloydd-sidebar-nav-evaluations"
              href={evaluationsHref}
              isActive={isEvaluationsActive}
            >
              <span className="fs-unmask">Evaluations</span>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        </Sidebar.Menu>
      </Collapsible>
    </Sidebar.MenuItem>
  );
};

// ============================================================
//     COMPONENTS
// ============================================================
/**
 * "Explore" nav group — Analytics and the Records collapsible (Entities, Evaluations).
 *
 * Mirrors the FE2.0 SideNav `NavList.explore` structure. Analytics points at the
 * home dashboard for now (Daffy's old analytics view — FE2.0's `/analytics` page
 * only exists in the beta dashboard). Conditions and links are otherwise ported
 * verbatim from the legacy sidebar (`~/dashboard/components/Sidebar/Sidebar.jsx`).
 */
export const ExploreGroup = () => {
  const { permissions } = useContext(UserContext);
  const { pathname } = useLocation();

  if (!permissions?.['READ:Entities:null']) return null;

  return (
    <Sidebar.Group>
      <Sidebar.GroupLabel>
        <span className="fs-unmask">Explore</span>
      </Sidebar.GroupLabel>
      <Sidebar.Menu>
        <Sidebar.MenuItem>
          <Sidebar.MenuButton
            as="a"
            data-dd-action-name="alloydd-sidebar-nav-analytics"
            href={`${window.location.origin}${routes.v3.dashboard.home.dashboard}`}
            isActive={isJourneyActive(routes.v3.dashboard.home.dashboard, pathname)}
            tooltip="Analytics"
          >
            <Icon icon={IconName.ChartColumn} size="small" />
            <span className="fs-unmask">Analytics</span>
          </Sidebar.MenuButton>
        </Sidebar.MenuItem>

        <RecordsNavItem />
      </Sidebar.Menu>
    </Sidebar.Group>
  );
};
